/**
 * Adaptive Card bodies for the Leader "Notify" affordance on the per-team-member table.
 *
 * The card is sent via a `webex_notification` queue task (see `useSubmitEnablementReminder`), whose
 * `card_content` the worker wraps as an `application/vnd.microsoft.card.adaptive` attachment. So these
 * builders return the raw Adaptive Card object (`type: 'AdaptiveCard'`), and each pairs with a plain-text
 * fallback the client shows when it can't render the card.
 *
 * The card recipient is always the row's own person (the user's decision):
 *   • a MANAGER row → the manager, reminded of the individual contributors under them still outstanding;
 *   • an IC row → that IC, reminded of how many enablement journey maps remain.
 *
 * The IC card reports the completion COUNT, not a list of map names — the Leader table sources completion
 * from the per-enrollment server rollup (a count), never per-map detail, so no map names are available
 * here without a per-user journey-map fetch.
 */

/** Adaptive Card JSON (loosely typed — the worker forwards it verbatim to Webex). */
export type AdaptiveCard = Record<string, any>;

const SCHEMA = 'http://adaptivecards.io/schemas/adaptive-card.json';

/** The button title for the deep-link back to the recipient's Leader dashboard. */
const LEADER_ACTION_TITLE = 'Open your Leader dashboard';

function card(body: AdaptiveCard[], leaderUrl?: string): AdaptiveCard {
  const base: AdaptiveCard = { type: 'AdaptiveCard', $schema: SCHEMA, version: '1.2', body };
  // A deep link to the recipient's Leader page (`<ui base url>/leader`), rendered as an OpenUrl action.
  if (leaderUrl) base.actions = [{ type: 'Action.OpenUrl', title: LEADER_ACTION_TITLE, url: leaderUrl }];
  return base;
}

/** Append the Leader-page link to a plain-text fallback (clients that can't render the card/actions). */
function withLeaderLink(text: string, leaderUrl?: string): string {
  return leaderUrl ? `${text}\n\n${LEADER_ACTION_TITLE}: ${leaderUrl}` : text;
}

function heading(text: string): AdaptiveCard {
  return { type: 'TextBlock', text, weight: 'Bolder', size: 'Medium', wrap: true };
}

function subtle(text: string): AdaptiveCard {
  return { type: 'TextBlock', text, isSubtle: true, spacing: 'None', wrap: true };
}

function body(text: string): AdaptiveCard {
  return { type: 'TextBlock', text, wrap: true };
}

/**
 * One outstanding DIRECT report on a manager reminder, with its own role-appropriate figure — a
 * sub-manager reads "X of Y users" (their team), an IC reads "X of Y journey maps".
 */
export interface ReminderReportLine {
  email: string;
  done: number;
  total: number;
  /** The unit the count is in: `users` for a sub-manager, `journey maps` for an IC. */
  unit: 'users' | 'journey maps';
}

/** `• email — X of Y unit` for one outstanding report. */
function reportLineText(r: ReminderReportLine): string {
  return `• ${r.email} — ${r.done} of ${r.total} ${r.unit}`;
}

/**
 * The manager reminder: the subtree tally plus a per-DIRECT-report breakdown of everyone still
 * outstanding — each sub-manager with their team's users-complete figure, each IC with their
 * journey-maps figure — so the manager sees exactly where the gap is, one line per report.
 */
export function buildManagerReminderCard(opts: {
  campaignName: string;
  done: number;
  total: number;
  reports: ReminderReportLine[];
  /** Absolute link back to the recipient's Leader dashboard (`<ui base url>/leader`). */
  leaderUrl?: string;
}): AdaptiveCard {
  const { campaignName, done, total, reports, leaderUrl } = opts;
  // Prefer the per-report breakdown count (one line per outstanding direct report). When no breakdown is
  // available (cohort mode carries no per-report detail), fall back to the outstanding-users figure so
  // the card never contradicts the "X of Y completed" headline with "0 still need attention".
  const remaining = reports.length > 0 ? reports.length : Math.max(0, total - done);
  const blocks: AdaptiveCard[] = [
    heading('Enablement completion reminder'),
    subtle(campaignName),
    body(
      `${done} of ${total} on your team have completed this campaign's enablement. ` +
        `${remaining} still ${remaining === 1 ? 'needs' : 'need'} attention.`,
    ),
  ];
  if (remaining > 0) {
    blocks.push({
      type: 'TextBlock',
      text: reports.map(reportLineText).join('\n'),
      wrap: true,
      spacing: 'Small',
    });
  }
  return card(blocks, leaderUrl);
}

/** The IC reminder: how many enablement journey maps they still have to finish. */
export function buildIcReminderCard(opts: {
  campaignName: string;
  done: number;
  total: number;
  /** Absolute link back to the recipient's Leader dashboard (`<ui base url>/leader`). */
  leaderUrl?: string;
}): AdaptiveCard {
  const { campaignName, done, total, leaderUrl } = opts;
  const remaining = Math.max(0, total - done);
  return card(
    [
      heading('Enablement completion reminder'),
      subtle(campaignName),
      body(
        `You've completed ${done} of ${total} enablement journey maps for this campaign. ` +
          `${remaining} ${remaining === 1 ? 'remains' : 'remain'} — please finish when you can.`,
      ),
    ],
    leaderUrl,
  );
}

/** Plain-text fallback for the manager card (shown by clients that don't render Adaptive Cards). */
export function managerReminderText(opts: {
  campaignName: string;
  done: number;
  total: number;
  reports: ReminderReportLine[];
  leaderUrl?: string;
}): string {
  const { campaignName, done, total, reports, leaderUrl } = opts;
  const lines = [
    `Enablement completion reminder — ${campaignName}`,
    `${done} of ${total} on your team have completed this campaign's enablement.`,
  ];
  if (reports.length) {
    lines.push('Still needing attention:');
    for (const r of reports) lines.push(reportLineText(r));
  }
  return withLeaderLink(lines.join('\n'), leaderUrl);
}

/** Plain-text fallback for the IC card. */
export function icReminderText(opts: {
  campaignName: string;
  done: number;
  total: number;
  leaderUrl?: string;
}): string {
  const { campaignName, done, total, leaderUrl } = opts;
  const remaining = Math.max(0, total - done);
  return withLeaderLink(
    `Enablement completion reminder — ${campaignName}\n` +
      `You've completed ${done} of ${total} enablement journey maps. ${remaining} remain.`,
    leaderUrl,
  );
}
